import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  SAVED_SEARCH_LIMIT,
  SAVED_SEARCH_STORAGE_KEY,
  buildSavedSearch,
  parseSavedSearchList,
  readSavedSearches,
  serializeSavedSearches,
} from "@/lib/savedSearches";
import type { SavedSearch } from "@/lib/savedSearches";
import type { AudienceType, SortKey, WorkFilters } from "@/types/work";

interface SavedSearchesContextValue {
  searches: SavedSearch[];
  /** 検索条件を保存する（上限に達している場合は保存しない） */
  addSearch: (name: string, filters: WorkFilters, sort: SortKey, mode: AudienceType) => { ok: boolean; message: string };
  renameSearch: (id: string, name: string) => void;
  removeSearch: (id: string) => void;
  clearSearches: () => void;
  /** バックアップのインポート用（キーは変更しない） */
  applyImported: (list: SavedSearch[], strategy: "merge" | "replace") => void;
  /** インポート時にストレージへ書き込んだ内容を状態へ反映する */
  hydrate: (list: SavedSearch[]) => void;
  count: number;
}

const SavedSearchesContext = createContext<SavedSearchesContextValue | null>(null);

export function SavedSearchesProvider({ children }: { children: ReactNode }) {
  const [searches, setSearches] = useState<SavedSearch[]>(() => readSavedSearches());

  useEffect(() => {
    try {
      window.localStorage.setItem(SAVED_SEARCH_STORAGE_KEY, serializeSavedSearches(searches));
    } catch {
      /* 保存できない環境ではセッション内のみ反映（サイト本体は動作を継続） */
    }
  }, [searches]);

  const addSearch = useCallback<SavedSearchesContextValue["addSearch"]>(
    (name, filters, sort, mode) => {
      if (searches.length >= SAVED_SEARCH_LIMIT) {
        return {
          ok: false,
          message: `保存できる検索条件は${SAVED_SEARCH_LIMIT}件までです。不要な条件を削除してください。`,
        };
      }
      const next = buildSavedSearch(name, filters, sort, mode);
      if (!next.name) {
        return { ok: false, message: "保存する条件の名前を入力してください。" };
      }
      setSearches((current) => [next, ...current].slice(0, SAVED_SEARCH_LIMIT));
      return { ok: true, message: `「${next.name}」として検索条件を保存しました。` };
    },
    [searches.length],
  );

  const renameSearch = useCallback((id: string, name: string) => {
    setSearches((current) =>
      current.map((item) => (item.id === id ? { ...item, name: name.trim().slice(0, 60) } : item)),
    );
  }, []);

  const removeSearch = useCallback((id: string) => {
    setSearches((current) => current.filter((item) => item.id !== id));
  }, []);

  const clearSearches = useCallback(() => setSearches([]), []);

  const hydrate = useCallback((list: SavedSearch[]) => {
    setSearches(parseSavedSearchList(list));
  }, []);

  const applyImported = useCallback((list: SavedSearch[], strategy: "merge" | "replace") => {
    const incoming = parseSavedSearchList(list);
    setSearches((current) => {
      if (strategy === "replace") return incoming.slice(0, SAVED_SEARCH_LIMIT);
      const merged = [...current];
      incoming.forEach((item) => {
        if (merged.length >= SAVED_SEARCH_LIMIT) return;
        const duplicated = merged.some((existing) => existing.id === item.id || existing.name === item.name);
        if (!duplicated) merged.push(item);
      });
      return merged;
    });
  }, []);

  const value = useMemo<SavedSearchesContextValue>(
    () => ({
      searches,
      addSearch,
      renameSearch,
      removeSearch,
      clearSearches,
      applyImported,
      hydrate,
      count: searches.length,
    }),
    [searches, addSearch, renameSearch, removeSearch, clearSearches, applyImported, hydrate],
  );

  return <SavedSearchesContext.Provider value={value}>{children}</SavedSearchesContext.Provider>;
}

export function useSavedSearches(): SavedSearchesContextValue {
  const context = useContext(SavedSearchesContext);
  if (!context) throw new Error("useSavedSearches must be used within SavedSearchesProvider");
  return context;
}